import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import axiosInstance from '../api/axiosInstance';
import { useAuth } from '../context/AuthContext';
import './WorkOrderDetailModal.css';

function WorkOrderDetailModal({ isOpen, onClose, workOrder, onDelete }) {
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editingCommentText, setEditingCommentText] = useState('');
  const { user } = useAuth();

  useEffect(() => {
    if (isOpen && workOrder) {
      const fetchComments = async () => {
        try {
          const response = await axiosInstance.get(`/comments/work-order/${workOrder.id}`);
          setComments(response.data);
        } catch (error) {
          console.error('Error fetching comments:', error);
        }
      };
      fetchComments();
    }
  }, [isOpen, workOrder]);

  if (!isOpen || !workOrder) return null;

  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    try {
      const response = await axiosInstance.post(`/comments/work-order/${workOrder.id}`, {
        content: newComment,
        author: user.name,
      });
      setComments([response.data, ...comments]);
      setNewComment('');
    } catch (error) {
      console.error('Error posting comment:', error);
      Swal.fire('오류', '댓글을 작성하는 중 오류가 발생했습니다.', 'error');
    }
  };

  const handleDeleteComment = async (commentId) => {
    Swal.fire({
      title: '정말 삭제하시겠습니까?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: '삭제',
      cancelButtonText: '취소',
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await axiosInstance.delete(`/comments/${commentId}`);
          setComments(comments.filter(c => c.id !== commentId));
        } catch (error) {
          console.error('Error deleting comment:', error);
          Swal.fire('오류', '댓글 삭제 중 오류가 발생했습니다.', 'error');
        }
      }
    });
  };

  const handleUpdateComment = async (commentId) => {
    if (!editingCommentText.trim()) return;
    try {
      const response = await axiosInstance.put(`/comments/${commentId}`, {
        content: editingCommentText,
      });
      setComments(comments.map(c => c.id === commentId ? response.data : c));
      cancelEditing();
    } catch (error) {
      console.error('Error updating comment:', error);
      Swal.fire('오류', '댓글 수정 중 오류가 발생했습니다.', 'error');
    }
  };

  const startEditing = (comment) => {
    setEditingCommentId(comment.id);
    setEditingCommentText(comment.content);
  };

  const cancelEditing = () => {
    setEditingCommentId(null);
    setEditingCommentText('');
  };

  const handleDeleteClick = () => {
    Swal.fire({
      title: '정말 삭제하시겠습니까?',
      text: "이 작업 지시는 되돌릴 수 없습니다!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: '삭제',
      cancelButtonText: '취소'
    }).then((result) => {
      if (result.isConfirmed) {
        onDelete(workOrder.id, workOrder.status);
        onClose();
      }
    });
  };

  const renderDetails = (taskRemarks) => {
    const { type, details: detailsJson } = workOrder;
    let details = {};

    try {
      if (detailsJson && typeof detailsJson === 'string') {
        details = JSON.parse(detailsJson);
      } else if (detailsJson && typeof detailsJson === 'object') {
        details = detailsJson;
      }
    } catch (e) {
      console.error("Failed to parse details JSON:", e);
    }

    switch (type) {
      case '배치':
        const { taskNumber, assignments } = details;
        const taskName = assignments && assignments.length > 0 ? assignments[0].taskName : 'N/A';

        let overallDateString = 'N/A';
        if (assignments && assignments.length > 0) {
          const sortedDates = assignments.map(a => a.assignmentDate).sort();
          const overallStartDate = sortedDates[0];
          const overallEndDate = sortedDates[sortedDates.length - 1];
          overallDateString = overallStartDate === overallEndDate ? overallStartDate : `${overallStartDate} ~ ${overallEndDate}`;
        }

        return (
          <div className="detail-section assignment-details">
            <div className="project-summary-info" style={{ textAlign: 'left', marginBottom: '20px' }}>
              <div><strong>배치날짜:</strong> {overallDateString}</div>
              <div><strong>공사번호:</strong> {taskNumber || 'N/A'}</div>
              <div><strong>공사명:</strong> {taskName}</div>
              {assignments && assignments.length > 0 && (
                <div>
                  <strong>책임:</strong> {assignments[0].assignedManagers} / <strong>보조:</strong> {assignments[0].assignedAssistants || '없음'}
                </div>
              )}
              {details.taskRemarks && (
                <div style={{ marginTop: '10px' }}>
                  <strong>비고:</strong>
                  <textarea
                    value={details.taskRemarks}
                    readOnly={true}
                    rows="3"
                    style={{ width: '100%', resize: 'vertical', marginTop: '5px' }}
                  />
                </div>
              )}
            </div>
            <h4 style={{ textAlign: 'left' }}>배치 내역</h4>
            {assignments && assignments.length > 0 ? (
              <ul className="assignment-list">
                {assignments.map((assignment, index) => (
                  <li key={index} className="assignment-item">
                    <div className="assignment-date">{assignment.assignmentDate}</div>
                    <div className="assignment-supervisors-inline">
                      <p><strong>책임감리원:</strong> {assignment.assignedManagers}</p>
                      <p><strong>보조감리원:</strong> {assignment.assignedAssistants || '없음'}</p>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <p>배치 내역이 없습니다.</p>
            )}
          </div>
        );
      case '시공통보':
        const { noticeDetails, principalSupervisor, assistantSupervisor } = details;
        return (
          <div className="detail-section" style={{ textAlign: 'left' }}>
            <h4>시공통보</h4>
            <p className="detail-content"><strong>공사명:</strong> {noticeDetails || '내용 없음'}</p>
            <p className="detail-content"><strong>책임감리원:</strong> {principalSupervisor || '미지정'}</p>
            <p className="detail-content"><strong>보조감리원:</strong> {assistantSupervisor || '없음'}</p>
          </div>
        );
      case '기타':
        const { otherTitle, otherDetails } = details;
        return (
          <div className="detail-section" style={{ textAlign: 'left' }}>
            <h4>기타</h4>
            <p className="detail-content" style={{ fontSize: '1.1em', fontWeight: 'bold' }}>{otherTitle || '제목 없음'}</p>
            <br/>
            <p style={{ fontWeight: 'bold', marginTop: '10px' }}>작업지시내용</p>
            <p className="detail-content">{otherDetails || '내용 없음'}</p>
          </div>
        );
      default:
        return <p>상세 정보가 없습니다.</p>;
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content work-order-detail-modal" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="modal-close-button">×</button>
        <h2>작업 지시 상세 정보</h2>
        {renderDetails()} 
        <div className="comments-section">
          <h5>Coments</h5>
          <div className="comments-list">
            {comments.map(comment => (
              <div key={comment.id} className="comment-item">
                {editingCommentId === comment.id ? (
                  <div className='comment-edit-form'>
                    <input 
                      type="text" 
                      value={editingCommentText} 
                      onChange={(e) => setEditingCommentText(e.target.value)} 
                    />
                    <button onClick={() => handleUpdateComment(comment.id)}>저장</button>
                    <button onClick={cancelEditing}>취소</button>
                  </div>
                ) : (
                  <>
                    <div className="comment-main">
                      <span className="comment-author">{comment.author}:</span>
                      <span className="comment-content">{comment.content}</span>
                    </div>
                    <div className="comment-actions">
                      <span className="comment-date">{new Date(comment.createdAt).toLocaleString()}</span>
                      {user && user.name === comment.author && (
                        <>
                          <button onClick={() => startEditing(comment)}>수정</button>
                          <button onClick={() => handleDeleteComment(comment.id)}>삭제</button>
                        </>
                      )}
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
          <form onSubmit={handleCommentSubmit} className="comment-form">
            <textarea
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="코멘트를 입력하세요..."
              rows="3"
            />
            <button type="submit">작성</button>
          </form>
        </div>
        <br/><hr/><br/>
        <div className="modal-actions">
          {user && user.role === 'ADMIN' && (
          <button onClick={handleDeleteClick} className="delete-button">삭제</button>
          )}
        </div>
      </div>
    </div>
  );
}

export default WorkOrderDetailModal;
